import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { Table } from 'primeng/table';
import { MessageService } from 'primeng/api';

import { environment } from 'src/environments/environment';

import { AgentAdminApiService } from 'src/app/services/agent-admin-api.service';
import { ClientApiService } from 'src/app/services/client-api.service';
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service';
import { AgentTeamMember } from 'src/app/shared/agent-admin';

/** Same 8-4-4-4-12 hex format the backend validates — catching a malformed
 *  paste client-side rather than round-tripping to the server first. */
const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * API-11 admin tab: `AgentTeamsIdentity` CRUD. Shows inactive members too
 * (with an active/inactive tag) rather than hiding them — the admin needs to
 * find and reactivate someone, not just see who's currently active. Toggling
 * `active` off in the row editor IS the deactivate action — no separate
 * delete/deactivate button, matching the backend having no delete route.
 *
 * `person_id` (a `Person.PERSID`) is the row's actual identity, picked
 * directly via `app-personselector` (`edit-user-access-tab.component.ts`'s
 * own "Пользователь" column uses the exact same component, same
 * `[parentCompany]` scoping) — `display_name` is a read-only field resolved
 * server-side (`webPersonsList`), refreshed optimistically here on selection
 * so the row doesn't show stale text until the next reload.
 *
 * Deliberately shows no WebYTO login/e-mail column (2026-08-12, after two
 * wrong attempts — see `GET /team-members`'s own comment in
 * `webyto_api-express4-TS-20250331/routes/AgentAdmin.ts` for the full
 * reasoning): `person_id -> SecurityGroupMembers` is 1:many, so there is no
 * single login to show, and the agent's own functional code never reads one
 * anyway. This tab's only job is the Teams-GUID <-> Person link.
 */
@Component({
  selector: 'app-edit-agent-team-members-tab',
  templateUrl: './edit-agent-team-members-tab.component.html',
  styleUrl: './edit-agent-team-members-tab.component.css',
  encapsulation: ViewEncapsulation.None
})
export class EditAgentTeamMembersTabComponent implements OnInit, OnDestroy {

  teamMembers: AgentTeamMember[] = [];
  teamMembersClone: AgentTeamMember[] = [];
  companyOptions: { label: string; value: number }[] = [];
  mainCompanyId: number = null;

  /** `true` while any row is being edited -- same single-row-edit-at-a-time
   *  convention as `edit-document-content.component.ts`'s own `editMode`.
   *  Gates the pencil ("start edit") button on every *other* row via
   *  `*ngIf="!editing && !editMode"` in the template, plus "Оновити" and
   *  "Новий учасник" here: reloading or adding a row mid-edit would replace/
   *  mutate `teamMembers` out from under `teamMembersClone`'s cancel-restore
   *  and PrimeNG's own `editingRowKeys` state, both keyed off values that
   *  would no longer match. */
  editMode: boolean = false;

  private loadQuery: Subscription = null;
  private companiesQuery: Subscription = null;
  private personQuery: Subscription = null;
  private saveQuery: Subscription = null;

  @ViewChild('ATM1') teamMembersTable: Table;

  constructor(
    private agentAdminApi: AgentAdminApiService,
    private cliApi: ClientApiService,
    private globalsettingsservice: GetglobalsettingsService,
    private messageService: MessageService,
  ) {
  }

  ngOnDestroy(): void {
    if (this.loadQuery) { this.loadQuery.unsubscribe(); }
    if (this.companiesQuery) { this.companiesQuery.unsubscribe(); }
    if (this.personQuery) { this.personQuery.unsubscribe(); }
    if (this.saveQuery) { this.saveQuery.unsubscribe(); }
  }

  ngOnInit(): void {
    this.mainCompanyId = this.globalsettingsservice.getMainCompanyId();
    this.loadTeamMembers();
    this.loadCompanyOptions();
  }

  /** Loads the "preferred company" display-name lookup for the output cell —
   *  editing itself uses `app-companyselector` directly, which resolves its
   *  own list. */
  loadCompanyOptions(): void {
    this.companiesQuery =
      this.cliApi.getCliChildList(this.mainCompanyId).subscribe({
        next: companies => { this.companyOptions = companies.map(c => ({ label: c.CLIENTSNIKNAME, value: c.CLIID })); },
        error: err => {
          console.error('*** EditAgentTeamMembersTabComponent loadCompanyOptions: Error: ' + err);
          // Non-fatal: the output cell just falls back to the raw ID, editing still works.
        }
      });
  }

  loadTeamMembers(): void {
    this.loadQuery =
      this.agentAdminApi.getTeamMembers().subscribe({
        next: members => {
          this.teamMembers = members.map(m => ({ ...m, uid: m.person_id, cloneIndex: 0 }));
          environment.production || console.table(this.teamMembers);
        },
        error: err => {
          console.error('*** EditAgentTeamMembersTabComponent loadTeamMembers: Error: ' + err);
          this.messageService.add(err?.status === 403
            ? { severity: 'error', summary: 'Недостаточно прав', detail: 'Требуется роль portal-admin для управления командою агента' }
            : { severity: 'error', summary: 'Не вдалося завантажити команду', detail: err?.message || 'Unknown error' });
        }
      });
  }

  /** Fired by `app-personselector`'s `(selectedPersonID)` output -- same
   *  `getPerListEntry(PERSID)` follow-up call `edit-user-access-tab.component.ts`'s
   *  `mainPersonSelected()` uses. Sets `person_id` (the actual identity) and
   *  optimistically refreshes `display_name` from the Person record. */
  onPersonSelected(member: AgentTeamMember, persId: number | null): void {
    member.person_id = persId;
    if (!persId) {
      member.display_name = '';
      return;
    }
    this.personQuery =
      this.cliApi.getPerListEntry(persId).pipe(
        filter(data => data && data.length > 0),
        map(data => data[0]),
      ).subscribe({
        next: person => {
          member.display_name = person.personFullName || person.personSaveAs || '';
        },
        error: err => {
          console.error('*** EditAgentTeamMembersTabComponent onPersonSelected: Error: ' + err);
          this.messageService.add({ severity: 'error', summary: 'Не вдалося завантажити дані користувача', detail: err?.message || 'Unknown error' });
        }
      });
  }

  addTeamMember(): void {
    const newRow: AgentTeamMember = {
      uid: -2, cloneIndex: 0, person_id: null,
      teams_user_id: '', display_name: '',
      preferred_company_id: null, is_escalation_contact: false, active: true,
    };
    this.teamMembers.push(newRow);
    this.onRowEditInit(newRow);
    this.teamMembersTable.initRowEdit(newRow);
  }

  onRowEditInit(member: AgentTeamMember): void {
    let newCloneIndex = 0;
    this.teamMembersClone.forEach(m => { newCloneIndex = Math.max(newCloneIndex, m.cloneIndex); });
    member.cloneIndex = ++newCloneIndex;
    this.teamMembersClone.push({ ...member });
    this.editMode = true;
  }

  onRowEditCancel(member: AgentTeamMember, index: number): void {
    if (member.uid === -2) {
      this.teamMembers.splice(index, 1);
    } else {
      this.teamMembers[index] = { ...this.teamMembersClone.find(m => m.cloneIndex === member.cloneIndex) };
      this.teamMembers[index].cloneIndex = 0;
    }
    this.teamMembersClone.splice(this.teamMembersClone.findIndex(m => m.cloneIndex === member.cloneIndex), 1);
    this.editMode = false;
  }

  onRowEditSave(member: AgentTeamMember): void {
    // The sibling pSaveEditableRow directive on the same button already exits
    // the table's own row-edit state synchronously on this click, regardless
    // of validation or the save's outcome below -- editMode must follow that,
    // not our async result, or a validation failure or a rejected save would
    // leave every other row's pencil (and "Оновити"/"Новий учасник") locked.
    this.editMode = false;

    if (!member.person_id) {
      this.messageService.add({ severity: 'error', summary: 'Помилка', detail: 'Потрібно обрати користувача' });
      return;
    }
    if (!GUID_RE.test((member.teams_user_id || '').trim())) {
      this.messageService.add({ severity: 'error', summary: 'Помилка', detail: 'teams_user_id має бути дійсним GUID (формат 8-4-4-4-12)' });
      return;
    }

    const isNew = member.uid === -2;
    this.saveQuery =
      (isNew ? this.agentAdminApi.createTeamMember(member) : this.agentAdminApi.updateTeamMember(member))
        .subscribe({
          next: result => {
            if (isNew && 'person_id' in result) { member.person_id = result.person_id; }
            member.uid = member.person_id;
            this.teamMembersClone.splice(this.teamMembersClone.findIndex(m => m.cloneIndex === member.cloneIndex), 1);
            member.cloneIndex = 0;
          },
          error: err => {
            console.error('*** EditAgentTeamMembersTabComponent onRowEditSave: Error: ' + err);
            this.messageService.add({ severity: 'error', summary: 'Не вдалося зберегти учасника', detail: err?.error?.message || err?.message || 'Unknown error' });
          }
        });
  }

  companyName(id: number | null): string {
    if (!id) { return ''; }
    return this.companyOptions.find(c => c.value === id)?.label || '';
  }

  reloadTable(): void {
    this.loadTeamMembers();
  }
}
